import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronRight,
  Folder,
  FolderOpen,
  HardDrive,
  RefreshCw,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { connectorQueries } from '@/lib/api/queries/connector'
import type { DriveItem } from '@/lib/api/types'

const FOLDER_MIME = 'application/vnd.google-apps.folder'

/** Drive's canonical alias for the user's My Drive root. */
export const DRIVE_ROOT_FOLDER_ID = 'root'

/** Magic id the API recognises to list `sharedWithMe = true` files. */
const SHARED_WITH_ME_ID = 'sharedWithMe'

export interface GoogleDriveDestination {
  /**
   * Either a real Drive folder ID or the literal `'root'` (which Drive
   * accepts as a parent and resolves to My Drive). The export mutation
   * forwards this value as-is — no special-casing required upstream.
   */
  folderId: string
  /** Human-readable path used in the footer summary. */
  label: string
}

interface BreadcrumbSegment {
  /** Drive folder id; `null` denotes "My Drive" root. */
  id: string | null
  name: string
}

interface GoogleDriveDestinationPickerProps {
  connectorId: string
  selected: GoogleDriveDestination | null
  onSelect: (destination: GoogleDriveDestination) => void
}

export function GoogleDriveDestinationPicker({
  connectorId,
  selected,
  onSelect,
}: GoogleDriveDestinationPickerProps) {
  // Root segment uses id: null. Descending into a Shared Drive, a folder, or
  // the "Shared with me" virtual root all just push the corresponding id.
  const [path, setPath] = useState<BreadcrumbSegment[]>([
    { id: null, name: 'My Drive' },
  ])
  const current = path[path.length - 1]
  const atRoot = current.id == null

  const browse = useQuery(
    connectorQueries.browseGoogle(connectorId, current.id ?? undefined),
  )

  const folders: DriveItem[] = (browse.data?.items ?? []).filter(
    (item) => item.mimeType === FOLDER_MIME,
  )
  const sharedDrives = browse.data?.sharedDrives ?? []

  const navigateInto = (id: string, name: string) => {
    setPath((prev) => [...prev, { id, name }])
  }

  const navigateTo = (index: number) => {
    setPath((prev) => prev.slice(0, index + 1))
  }

  const labelForPath = (folderName: string) =>
    [...path.filter((s) => s.id != null).map((s) => s.name), folderName].join(
      ' / ',
    )

  return (
    // `flex-1 min-h-0` lets this picker stretch to fill the remaining
    // DialogContent height (the parent already uses `flex flex-col max-h-[80vh]`)
    // AND shrink below content height so the inner scroll region can clip
    // the list instead of overflowing past the dialog footer.
    <div className="flex flex-1 min-h-0 flex-col gap-3">
      <Breadcrumb path={path} onNavigate={navigateTo} />

      <div className="flex-1 min-h-0">
        {browse.isPending ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 py-2 px-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="h-4 flex-1 max-w-[220px]" />
              </div>
            ))}
          </div>
        ) : browse.isError ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-muted-foreground mb-3">
              Couldn&apos;t load Google Drive folders.
            </p>
            <Button variant="default" size="sm" onClick={() => browse.refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          </div>
        ) : (
          (() => {
            // At root we ALWAYS render the list — even with zero My-Drive
            // folders and zero Shared Drives — because the "Shared with me"
            // entry must remain reachable as a separate nav row below.
            // The empty-state copy is only shown for nested views.
            const showListbox = atRoot || folders.length > 0
            const isSwmView = current.id === SHARED_WITH_ME_ID
            return (
              <ScrollArea className="h-full pr-2">
                {showListbox && (
                  <ul role="listbox" aria-label="Google Drive folders" className="flex flex-col">
                    {/* My Drive personal folders */}
                    {folders.map((folder) => (
                      <FolderRow
                        key={folder.id}
                        id={folder.id}
                        name={folder.name}
                        icon="folder"
                        selected={selected}
                        onSelect={() =>
                          onSelect({
                            folderId: folder.id,
                            label: labelForPath(folder.name),
                          })
                        }
                        onDrillDown={() => navigateInto(folder.id, folder.name)}
                      />
                    ))}

                    {/* At root only: Shared Drives as selectable destinations. */}
                    {atRoot && sharedDrives.length > 0 && (
                      <li
                        role="presentation"
                        className="px-2 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide"
                      >
                        Shared drives
                      </li>
                    )}
                    {atRoot && sharedDrives.map((sd) => (
                      <FolderRow
                        key={`sd-${sd.id}`}
                        id={sd.id}
                        name={sd.name}
                        icon="shared-drive"
                        selected={selected}
                        onSelect={() =>
                          onSelect({ folderId: sd.id, label: sd.name })
                        }
                        onDrillDown={() => navigateInto(sd.id, sd.name)}
                      />
                    ))}
                  </ul>
                )}

                {/* Shared with me is rendered OUTSIDE the listbox: it's a
                    nav-only entry (you can't drop a Doc into the virtual
                    flat view), so a <button> matches its semantics better
                    than role="option". Always shown at root, never lost
                    behind the empty state. */}
                {atRoot && (
                  <div className={showListbox ? 'mt-2 pt-2 border-t' : ''}>
                    <button
                      type="button"
                      onClick={() => navigateInto(SHARED_WITH_ME_ID, 'Shared with me')}
                      className="flex items-center gap-2 w-full px-2 py-2 rounded-md hover:bg-muted/50 text-left"
                    >
                      <Users className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                      <span className="flex-1 text-sm">Shared with me</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                    </button>
                  </div>
                )}

                {/* Empty state — only shown when truly nothing to render. */}
                {!showListbox && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <HardDrive className="w-8 h-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {isSwmView ? (
                        // Only folders are eligible destinations; files shared
                        // with you exist but are filtered out, so "no shared
                        // folders" is more accurate than "nothing shared".
                        <>No shared folders you can use as a destination.</>
                      ) : (
                        <>
                          No sub-folders here. You can still create the Doc in&nbsp;
                          <span className="font-medium">{current.name}</span>.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </ScrollArea>
            )
          })()
        )}
      </div>

      {/* Quick action: pick the *current* container without descending. At
          root this means My Drive itself; in a folder/Shared Drive it picks
          that container. Suppressed for the "Shared with me" virtual view
          since it isn't a real folder you can write to. */}
      {current.id !== SHARED_WITH_ME_ID && (
        <div className="border-t pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onSelect({
                folderId: current.id ?? DRIVE_ROOT_FOLDER_ID,
                label:
                  current.id == null
                    ? 'My Drive'
                    : path
                        .filter((s) => s.id != null)
                        .map((s) => s.name)
                        .join(' / '),
              })
            }
          >
            <FolderOpen className="w-4 h-4 mr-2" aria-hidden />
            Use {current.id == null ? 'My Drive' : `this (${current.name})`}
          </Button>
        </div>
      )}
    </div>
  )
}

function Breadcrumb({
  path,
  onNavigate,
}: {
  path: BreadcrumbSegment[]
  onNavigate: (index: number) => void
}) {
  return (
    <nav aria-label="Drive navigation" className="flex flex-wrap items-center gap-1 text-sm">
      {path.map((seg, idx) => {
        const isLast = idx === path.length - 1
        return (
          <span key={`${seg.id ?? 'root'}-${idx}`} className="flex items-center gap-1">
            {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
            {isLast ? (
              <span className="font-medium">{seg.name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(idx)}
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                {seg.name}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}

function FolderRow({
  id,
  name,
  icon,
  selected,
  onSelect,
  onDrillDown,
}: {
  id: string
  name: string
  icon: 'folder' | 'shared-drive' | 'shared-with-me'
  selected: GoogleDriveDestination | null
  /** Omit to make this row navigation-only (not a valid destination). */
  onSelect?: () => void
  onDrillDown: () => void
}) {
  const isSelected = selected?.folderId === id
  const Icon = icon === 'shared-drive' ? HardDrive : icon === 'shared-with-me' ? Users : Folder

  return (
    <li
      role="option"
      aria-selected={isSelected}
      className={cn(
        'flex items-center gap-2 px-2 py-2 rounded-md border border-transparent',
        isSelected && 'border-primary bg-primary/5',
      )}
    >
      {onSelect ? (
        <input
          type="radio"
          name="drive-doc-destination"
          aria-label={`Use ${name} as destination`}
          checked={isSelected}
          onChange={onSelect}
          className="w-4 h-4 accent-primary"
        />
      ) : (
        <span className="w-4 h-4" aria-hidden />
      )}

      <Icon className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />

      <button
        type="button"
        className="flex-1 text-left text-sm hover:underline truncate"
        onClick={onDrillDown}
      >
        {name}
      </button>

      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
    </li>
  )
}
